import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  Building2,
  CheckCircle2,
  ChevronDown,
  Clock3,
  FileText,
  Globe2,
  Loader2,
  LockKeyhole,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from "lucide-react";

import TitleButtons from "../../../../../components/TitleButtons";
import { useTheme } from "../../../../../context/ThemeContext";
import { useAuth } from "../../../../../context/auth-context";

type SectionKey =
  | "visao-geral"
  | "pendencias"
  | "aguardando-validacao"
  | "vencidos"
  | "atualizados"
  | "clientes-portais";

type StatusCadastral =
  | "regular"
  | "pendente_documental"
  | "documento_vencido"
  | "aguardando_validacao"
  | "bloqueado"
  | "nao_cadastrado"
  | "nao_avaliado";

type StatusDocumentoPortal =
  | "atualizado"
  | "pendente_anexo"
  | "renovacao_pendente"
  | "vencido"
  | "aguardando_validacao"
  | "nao_exigido"
  | "nao_avaliado";

type PortalCliente = {
  id: number;
  clienteNome: string;
  clienteRazaoSocial: string;
  clienteCnpj: string;
  codigoInterno: string;
  portalNome: string;
  portalUrl: string;
  usuarioReferencia: string;
  areaResponsavel: string;
  responsavelNome: string;
  statusCadastral: StatusCadastral;
  ultimoAcesso: string | null;
  ultimaAtualizacao: string | null;
  documentosExigidos: number;
  documentosAtualizados: number;
  documentosPendentes: number;
  documentosVencidos: number;
  documentosAguardandoValidacao: number;
  documentosRenovacaoPendente: number;
  observacao: string | null;
  ativo: boolean;
  documentos: DocumentoExigidoPortal[];
};

type DocumentoExigidoPortal = {
  id: number;
  documentoNome: string;
  categoria: string;
  orgaoEmissor: string;
  status: StatusDocumentoPortal;
  validade: string | null;
  arquivoNome: string | null;
  atualizadoEm: string | null;
  responsavel: string;
};

type DocumentoCatalogo = {
  id: number;
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  responsavelNome: string;
  statusValidade: string;
};

type PortalFilterState = {
  cliente: string;
  portal: string;
  statusCadastral: string;
  responsavel: string;
  pendencia: string;
};

type RegisterPortalForm = {
  clienteNome: string;
  clienteRazaoSocial: string;
  clienteCnpj: string;
  codigoInterno: string;
  portalNome: string;
  portalUrl: string;
  usuarioReferencia: string;
  areaResponsavel: string;
  responsavelNome: string;
  statusCadastral: StatusCadastral;
  observacao: string;
  documentosExigidosIds: number[];
};

type ApiPortaisClientesResponse = {
  ok?: boolean;
  items?: PortalCliente[];
  erro?: string;
};

type ApiPostPortalClienteResponse = {
  ok?: boolean;
  portal?: PortalCliente;
  erro?: string;
};

type ApiDocumentosResponse = {
  ok?: boolean;
  items?: any[];
  documentos?: any[];
  rows?: any[];
  data?: any[];
  erro?: string;
};

type GovernancaPortaisClientesLayoutProps = {
  section?: SectionKey;
};

const API_BASE = "https://api.logvi-havox.com";
const API_PORTAIS_CLIENTES_URL = `${API_BASE}/template/governanca/portais-clientes`;
const API_DOCUMENTOS_URL = `${API_BASE}/template/governanca/documentos`;

const BASE_PATH = "/governanca-qualidade-e-compliance/portais-de-clientes";
const DEPARTMENT_COLOR = "#2f3f59";

const sections: Array<{
  key: SectionKey;
  label: string;
  path: string;
  icon: React.ReactNode;
}> = [
  {
    key: "visao-geral",
    label: "Visão geral",
    path: `${BASE_PATH}/visao-geral`,
    icon: <ShieldCheck size={14} />,
  },
  {
    key: "pendencias",
    label: "Pendências",
    path: `${BASE_PATH}/pendencias`,
    icon: <AlertTriangle size={14} />,
  },
  {
    key: "aguardando-validacao",
    label: "Aguardando validação",
    path: `${BASE_PATH}/aguardando-validacao`,
    icon: <Clock3 size={14} />,
  },
  {
    key: "vencidos",
    label: "Vencidos",
    path: `${BASE_PATH}/vencidos`,
    icon: <FileText size={14} />,
  },
  {
    key: "atualizados",
    label: "Atualizados",
    path: `${BASE_PATH}/atualizados`,
    icon: <CheckCircle2 size={14} />,
  },
  {
    key: "clientes-portais",
    label: "Clientes / Portais",
    path: `${BASE_PATH}/clientes-portais`,
    icon: <Globe2 size={14} />,
  },
];

const initialFilters: PortalFilterState = {
  cliente: "",
  portal: "",
  statusCadastral: "",
  responsavel: "",
  pendencia: "",
};

const initialRegisterPortalForm: RegisterPortalForm = {
  clienteNome: "",
  clienteRazaoSocial: "",
  clienteCnpj: "",
  codigoInterno: "",
  portalNome: "",
  portalUrl: "",
  usuarioReferencia: "",
  areaResponsavel: "",
  responsavelNome: "",
  statusCadastral: "nao_avaliado",
  observacao: "",
  documentosExigidosIds: [],
};

function normalizeTextSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function toNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toNullableString(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str ? str : null;
}

function getEmpresaIdFromSessao(sessao: any): number | null {
  const value =
    sessao?.empresa_id ??
    sessao?.empresaId ??
    sessao?.empresa_atual_id ??
    sessao?.empresaSelecionadaId ??
    sessao?.empresa?.id;

  if (value === undefined || value === null || value === "") {
    return null;
  }

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getModuloIdFromSessao(sessao: any): number | null {
  return getModuloIdByKindFromSessao(sessao, "portais");
}

function getModuloDocumentosIdFromSessao(sessao: any): number | null {
  return getModuloIdByKindFromSessao(sessao, "documentos");
}

function getModuloIdByKindFromSessao(
  sessao: any,
  kind: "portais" | "documentos"
): number | null {
  const direct =
    kind === "portais"
      ? sessao?.modulo_id ??
        sessao?.moduloId ??
        sessao?.modulo_atual_id ??
        sessao?.moduloSelecionadoId ??
        sessao?.modulo?.id
      : null;

  if (direct !== undefined && direct !== null && direct !== "") {
    const n = Number(direct);
    if (Number.isFinite(n)) return n;
  }

  const isGovernancaDepartment = (dep: any) => {
    const depSlug = normalizeTextSearch(dep?.slug);
    const depNome = normalizeTextSearch(dep?.nome);
    const depRouteKey = normalizeTextSearch(dep?.routeKey ?? dep?.route_key);
    const depCodigo = normalizeTextSearch(dep?.codigo);

    return (
      depSlug.includes("governanca") ||
      depSlug.includes("governança") ||
      depNome.includes("governanca") ||
      depNome.includes("governança") ||
      depNome.includes("compliance") ||
      depRouteKey.includes("governanca") ||
      depRouteKey.includes("governança") ||
      depCodigo.includes("governanca") ||
      depCodigo.includes("governança")
    );
  };

  const matchesKind = (mod: any) => {
    const id = mod?.id;

    if (id === undefined || id === null || id === "") {
      return false;
    }

    const codigo = normalizeTextSearch(mod?.codigo);
    const label = normalizeTextSearch(mod?.label);
    const nome = normalizeTextSearch(mod?.nome);
    const routeKey = normalizeTextSearch(mod?.routeKey ?? mod?.route_key);
    const actionKey = normalizeTextSearch(mod?.actionKey ?? mod?.action_key);

    const full = [codigo, label, nome, routeKey, actionKey].join(" ");

    if (kind === "portais") {
      return (
        full.includes("portais") ||
        full.includes("portal") ||
        full.includes("clientes") ||
        full.includes("portais-de-clientes") ||
        full.includes("portais_clientes")
      );
    }

    return (
      full.includes("documentos") ||
      full.includes("documento") ||
      full.includes("certidoes") ||
      full.includes("certidões") ||
      full.includes("certidoes-documentos") ||
      full.includes("certidoes-e-documentos")
    );
  };

  const extractFromDepartments = (departamentos: any[]): number | null => {
    for (const dep of departamentos) {
      if (!isGovernancaDepartment(dep)) continue;

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];
      const modulo = modulos.find((mod: any) => matchesKind(mod));

      const id = modulo?.id;

      if (id !== undefined && id !== null && id !== "") {
        const n = Number(id);
        if (Number.isFinite(n)) return n;
      }
    }

    return null;
  };

  const departamentosDiretos = Array.isArray(sessao?.departamentos_acessiveis)
    ? sessao.departamentos_acessiveis
    : [];

  const fromDepartamentosDiretos = extractFromDepartments(departamentosDiretos);

  if (fromDepartamentosDiretos) {
    return fromDepartamentosDiretos;
  }

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosComModulos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  if (enderecoSelecionadoId) {
    const enderecoSelecionado = enderecosComModulos.find(
      (end: any) => String(end?.id ?? "") === enderecoSelecionadoId
    );

    const departamentosEndereco = Array.isArray(
      enderecoSelecionado?.departamentos_acessiveis
    )
      ? enderecoSelecionado.departamentos_acessiveis
      : [];

    const fromEnderecoSelecionado = extractFromDepartments(
      departamentosEndereco
    );

    if (fromEnderecoSelecionado) {
      return fromEnderecoSelecionado;
    }
  }

  for (const endereco of enderecosComModulos) {
    const departamentosEndereco = Array.isArray(
      endereco?.departamentos_acessiveis
    )
      ? endereco.departamentos_acessiveis
      : [];

    const fromEndereco = extractFromDepartments(departamentosEndereco);

    if (fromEndereco) {
      return fromEndereco;
    }
  }

  return null;
}

function getAuthToken(auth: any): string | null {
  return (
    toNullableString(auth?.token) ||
    toNullableString(auth?.accessToken) ||
    toNullableString(auth?.access_token) ||
    toNullableString(auth?.jwt) ||
    toNullableString(auth?.sessao?.token)
  );
}

function normalizePortalFromApi(item: Partial<PortalCliente>): PortalCliente {
  return {
    id: toNumber(item.id),
    clienteNome: toNullableString(item.clienteNome) || "Cliente sem nome",
    clienteRazaoSocial:
      toNullableString(item.clienteRazaoSocial) ||
      toNullableString(item.clienteNome) ||
      "Não informado",
    clienteCnpj: toNullableString(item.clienteCnpj) || "Não informado",
    codigoInterno: toNullableString(item.codigoInterno) || "",
    portalNome: toNullableString(item.portalNome) || "Portal sem nome",
    portalUrl: toNullableString(item.portalUrl) || "",
    usuarioReferencia: toNullableString(item.usuarioReferencia) || "",
    areaResponsavel: toNullableString(item.areaResponsavel) || "Governança",
    responsavelNome: toNullableString(item.responsavelNome) || "Não informado",
    statusCadastral:
      item.statusCadastral === "regular" ||
      item.statusCadastral === "pendente_documental" ||
      item.statusCadastral === "documento_vencido" ||
      item.statusCadastral === "aguardando_validacao" ||
      item.statusCadastral === "bloqueado" ||
      item.statusCadastral === "nao_cadastrado" ||
      item.statusCadastral === "nao_avaliado"
        ? item.statusCadastral
        : "nao_avaliado",
    ultimoAcesso: toNullableString(item.ultimoAcesso),
    ultimaAtualizacao: toNullableString(item.ultimaAtualizacao),
    documentosExigidos: toNumber(item.documentosExigidos),
    documentosAtualizados: toNumber(item.documentosAtualizados),
    documentosPendentes: toNumber(item.documentosPendentes),
    documentosVencidos: toNumber(item.documentosVencidos),
    documentosAguardandoValidacao: toNumber(item.documentosAguardandoValidacao),
    documentosRenovacaoPendente: toNumber(item.documentosRenovacaoPendente),
    observacao: toNullableString(item.observacao),
    ativo: item.ativo !== false,
    documentos: Array.isArray(item.documentos) ? item.documentos : [],
  };
}

function normalizeDocumentoFromApi(item: any): DocumentoCatalogo | null {
  const id = toNumber(
    item?.id ??
      item?.documentoId ??
      item?.documento_id ??
      item?.documento?.id ??
      null
  );

  if (!id) return null;

  return {
    id,
    nome:
      toNullableString(item?.nome) ||
      toNullableString(item?.documentoNome) ||
      toNullableString(item?.titulo) ||
      `Documento ${id}`,
    categoria:
      toNullableString(item?.categoria) ||
      toNullableString(item?.categoriaNome) ||
      "Sem categoria",
    orgaoEmissor:
      toNullableString(item?.orgaoEmissor) ||
      toNullableString(item?.orgao_emissor) ||
      "Não informado",
    responsavelNome:
      toNullableString(item?.responsavelNome) ||
      toNullableString(item?.responsavel_nome) ||
      toNullableString(item?.responsavel) ||
      "Não informado",
    statusValidade:
      toNullableString(item?.statusValidade) ||
      toNullableString(item?.status_validade) ||
      "nao_avaliado",
  };
}

function getDocumentosArrayFromApi(data: ApiDocumentosResponse | null): any[] {
  if (!data) return [];

  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.documentos)) return data.documentos;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.data)) return data.data;

  return [];
}

function getSectionFromPath(pathname: string): SectionKey {
  const path = pathname.toLowerCase();

  if (path.includes("pendencias")) return "pendencias";
  if (path.includes("aguardando-validacao")) return "aguardando-validacao";
  if (path.includes("vencidos")) return "vencidos";
  if (path.includes("atualizados")) return "atualizados";
  if (path.includes("clientes-portais")) return "clientes-portais";

  return "visao-geral";
}

function plural(value: number, singular: string, pluralText: string) {
  return value === 1 ? singular : pluralText;
}

function getStatusCadastralLabel(status: StatusCadastral) {
  switch (status) {
    case "regular":
      return "Regular";
    case "pendente_documental":
      return "Pendente documental";
    case "documento_vencido":
      return "Documento vencido";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "bloqueado":
      return "Bloqueado";
    case "nao_cadastrado":
      return "Não cadastrado";
    case "nao_avaliado":
      return "Não avaliado";
    default:
      return "Indefinido";
  }
}

function getStatusCadastralVisual(status: StatusCadastral) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };
    case "pendente_documental":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };
    case "documento_vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };
    case "aguardando_validacao":
      return {
        background: "#f5f3ff",
        color: "#6d28d9",
        border: "#ddd6fe",
        icon: <Clock3 size={12} />,
      };
    case "bloqueado":
      return {
        background: "#fff1f2",
        color: "#9f1239",
        border: "#fecdd3",
        icon: <LockKeyhole size={12} />,
      };
    case "nao_cadastrado":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };
    case "nao_avaliado":
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function hasPendencia(portal: PortalCliente) {
  return (
    portal.documentosPendentes > 0 ||
    portal.documentosVencidos > 0 ||
    portal.documentosAguardandoValidacao > 0 ||
    portal.documentosRenovacaoPendente > 0 ||
    portal.statusCadastral !== "regular"
  );
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "pendencias":
      return "Pendências documentais";
    case "aguardando-validacao":
      return "Aguardando validação";
    case "vencidos":
      return "Documentos vencidos em portais";
    case "atualizados":
      return "Portais atualizados";
    case "clientes-portais":
      return "Clientes e portais";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span
      style={{
        ...styles.badge,
        background,
        color,
        borderColor: border,
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const RegisterPortalOverlay: React.FC<{
  open: boolean;
  form: RegisterPortalForm;
  documentosCatalogo: DocumentoCatalogo[];
  documentosLoading: boolean;
  documentosError: string | null;
  departmentColor: string;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onChange: <K extends keyof RegisterPortalForm>(
    key: K,
    value: RegisterPortalForm[K]
  ) => void;
  onAddDocumento: (documentoId: number) => void;
  onRemoveDocumento: (documentoId: number) => void;
  onSubmit: () => void;
}> = ({
  open,
  form,
  documentosCatalogo,
  documentosLoading,
  documentosError,
  departmentColor,
  saving,
  error,
  onClose,
  onChange,
  onAddDocumento,
  onRemoveDocumento,
  onSubmit,
}) => {
  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [documentSearch, setDocumentSearch] = useState("");

  useEffect(() => {
    if (!open) {
      setDocumentDropdownOpen(false);
      setDocumentSearch("");
    }
  }, [open]);

  if (!open) return null;

  const selectedDocumentos = documentosCatalogo.filter((doc) =>
    form.documentosExigidosIds.includes(doc.id)
  );

  const availableDocumentos = documentosCatalogo.filter(
    (doc) => !form.documentosExigidosIds.includes(doc.id)
  );

  const search = normalizeTextSearch(documentSearch);

  const filteredAvailableDocumentos = availableDocumentos.filter((doc) => {
    if (!search) return true;

    return [
      doc.nome,
      doc.categoria,
      doc.orgaoEmissor,
      doc.responsavelNome,
      doc.statusValidade,
    ].some((field) => normalizeTextSearch(field).includes(search));
  });

  const canSubmit =
    String(form.clienteNome ?? "").trim().length > 0 &&
    String(form.portalNome ?? "").trim().length > 0 &&
    !saving;

  return (
    <div style={styles.overlayBackdrop}>
      <div style={styles.overlayPanel}>
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>Governança cadastral</div>
            <h2 style={styles.overlayTitle}>Registrar portal de cliente</h2>
            <p style={styles.overlaySubtitle}>
              Cadastre o portal e adicione, pela lista de documentos existentes,
              quais documentos são exigidos nesse ambiente.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
            disabled={saving}
          >
            <X size={17} />
          </button>
        </div>

        {error && (
          <div style={styles.overlayError}>
            <AlertTriangle size={15} />
            <span>{error}</span>
          </div>
        )}

        <div style={styles.formGrid}>
          <div style={styles.formSectionTitle}>
            <Building2 size={14} />
            <span>Cliente</span>
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Nome do cliente *</label>
            <input
              style={styles.formInput}
              value={form.clienteNome}
              onChange={(event) => onChange("clienteNome", event.target.value)}
              placeholder="Ex.: Bravante / Marlin Navegação"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Código interno</label>
            <input
              style={styles.formInput}
              value={form.codigoInterno}
              onChange={(event) =>
                onChange("codigoInterno", event.target.value)
              }
              placeholder="Ex.: CLI-MARLIN"
              disabled={saving}
            />
          </div>

          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>Razão social</label>
            <input
              style={styles.formInput}
              value={form.clienteRazaoSocial}
              onChange={(event) =>
                onChange("clienteRazaoSocial", event.target.value)
              }
              placeholder="Ex.: MARLIN NAVEGAÇÃO S.A."
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>CNPJ / Identificação</label>
            <input
              style={styles.formInput}
              value={form.clienteCnpj}
              onChange={(event) => onChange("clienteCnpj", event.target.value)}
              placeholder="Ex.: 00.000.000/0000-00"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Status cadastral</label>
            <select
              style={styles.formInput}
              value={form.statusCadastral}
              onChange={(event) =>
                onChange(
                  "statusCadastral",
                  event.target.value as StatusCadastral
                )
              }
              disabled={saving}
            >
              <option value="regular">Regular</option>
              <option value="pendente_documental">Pendente documental</option>
              <option value="documento_vencido">Documento vencido</option>
              <option value="aguardando_validacao">Aguardando validação</option>
              <option value="bloqueado">Bloqueado</option>
              <option value="nao_cadastrado">Não cadastrado</option>
              <option value="nao_avaliado">Não avaliado</option>
            </select>
          </div>

          <div style={styles.formSectionTitle}>
            <Globe2 size={14} />
            <span>Portal</span>
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Nome do portal *</label>
            <input
              style={styles.formInput}
              value={form.portalNome}
              onChange={(event) => onChange("portalNome", event.target.value)}
              placeholder="Ex.: Meritum"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Usuário / referência</label>
            <input
              style={styles.formInput}
              value={form.usuarioReferencia}
              onChange={(event) =>
                onChange("usuarioReferencia", event.target.value)
              }
              placeholder="Ex.: service@havox.com.br"
              disabled={saving}
            />
          </div>

          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>URL do portal</label>
            <input
              style={styles.formInput}
              value={form.portalUrl}
              onChange={(event) => onChange("portalUrl", event.target.value)}
              placeholder="https://..."
              disabled={saving}
            />
          </div>

          <div style={styles.formSectionTitle}>
            <FileText size={14} />
            <span>Documentos exigidos pelo portal</span>
          </div>

          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>Adicionar documento exigido</label>

            <div style={styles.docSelectShell}>
              <button
                type="button"
                style={styles.docSelectButton}
                onClick={() => setDocumentDropdownOpen((current) => !current)}
                disabled={saving || documentosLoading}
              >
                <span style={styles.docSelectButtonText}>
                  {documentosLoading
                    ? "Carregando documentos..."
                    : "Selecionar documento existente"}
                </span>

                {documentosLoading ? (
                  <Loader2 size={15} />
                ) : (
                  <ChevronDown size={15} />
                )}
              </button>

              {documentDropdownOpen && (
                <div style={styles.docDropdown}>
                  <div style={styles.docSearchBox}>
                    <Search size={14} />
                    <input
                      style={styles.docSearchInput}
                      value={documentSearch}
                      onChange={(event) =>
                        setDocumentSearch(event.target.value)
                      }
                      placeholder="Buscar por documento, categoria ou órgão..."
                      autoFocus
                    />
                  </div>

                  {documentosError && (
                    <div style={styles.docDropdownError}>
                      <AlertTriangle size={14} />
                      <span>{documentosError}</span>
                    </div>
                  )}

                  {!documentosError &&
                    !documentosLoading &&
                    filteredAvailableDocumentos.length === 0 && (
                      <div style={styles.docDropdownEmpty}>
                        Nenhum documento disponível para adicionar.
                      </div>
                    )}

                  {!documentosError &&
                    !documentosLoading &&
                    filteredAvailableDocumentos.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        style={styles.docOption}
                        onClick={() => {
                          onAddDocumento(doc.id);
                          setDocumentSearch("");
                          setDocumentDropdownOpen(false);
                        }}
                      >
                        <span style={styles.docOptionMain}>{doc.nome}</span>
                        <span style={styles.docOptionMeta}>
                          {doc.categoria} • {doc.orgaoEmissor}
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div style={styles.selectedDocsListPanel}>
              <div style={styles.selectedDocsListHeader}>
                <div>
                  <strong style={styles.selectedDocsTitle}>
                    Documentos adicionados
                  </strong>
                  <span style={styles.selectedDocsHint}>
                    {selectedDocumentos.length === 0
                      ? "Nenhum documento exigido selecionado."
                      : `${selectedDocumentos.length} documento(s) exigido(s) selecionado(s).`}
                  </span>
                </div>
              </div>

              {selectedDocumentos.length === 0 ? (
                <div style={styles.selectedDocsEmpty}>
                  Selecione no dropdown acima os documentos exigidos por este
                  portal. O portal também pode ser salvo sem documentos e
                  ajustado depois.
                </div>
              ) : (
                <div style={styles.selectedDocsTableWrap}>
                  <table style={styles.selectedDocsTable}>
                    <thead>
                      <tr>
                        <th style={styles.selectedDocsTh}>Documento</th>
                        <th style={styles.selectedDocsTh}>Categoria</th>
                        <th style={styles.selectedDocsTh}>Órgão emissor</th>
                        <th style={styles.selectedDocsTh}>Status</th>
                        <th style={styles.selectedDocsThAction}>Remover</th>
                      </tr>
                    </thead>

                    <tbody>
                      {selectedDocumentos.map((doc) => (
                        <tr key={doc.id} style={styles.selectedDocsTr}>
                          <td style={styles.selectedDocsTd}>
                            <div style={styles.selectedDocName}>{doc.nome}</div>
                            <div style={styles.selectedDocMeta}>
                              Responsável: {doc.responsavelNome}
                            </div>
                          </td>

                          <td style={styles.selectedDocsTd}>{doc.categoria}</td>

                          <td style={styles.selectedDocsTd}>
                            {doc.orgaoEmissor}
                          </td>

                          <td style={styles.selectedDocsTd}>
                            <span style={styles.docStatusPill}>
                              {doc.statusValidade}
                            </span>
                          </td>

                          <td style={styles.selectedDocsTdAction}>
                            <button
                              type="button"
                              style={styles.removeDocButton}
                              onClick={() => onRemoveDocumento(doc.id)}
                              disabled={saving}
                              title="Remover documento exigido"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div style={styles.formSectionTitle}>
            <UserRound size={14} />
            <span>Responsabilidade interna</span>
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Área responsável</label>
            <input
              style={styles.formInput}
              value={form.areaResponsavel}
              onChange={(event) =>
                onChange("areaResponsavel", event.target.value)
              }
              placeholder="Ex.: Governança"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Responsável</label>
            <input
              style={styles.formInput}
              value={form.responsavelNome}
              onChange={(event) =>
                onChange("responsavelNome", event.target.value)
              }
              placeholder="Ex.: Guilherme Brito"
              disabled={saving}
            />
          </div>

          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>Observação</label>
            <textarea
              style={styles.formTextarea}
              value={form.observacao}
              onChange={(event) => onChange("observacao", event.target.value)}
              placeholder="Observações sobre cadastro, acesso, pendências ou regra documental do cliente..."
              disabled={saving}
            />
          </div>
        </div>

        <div style={styles.overlayFooter}>
          <button
            type="button"
            style={styles.cancelButton}
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="button"
            style={{
              ...styles.saveButton,
              background: canSubmit ? departmentColor : "#94a3b8",
              borderColor: canSubmit ? departmentColor : "#94a3b8",
            }}
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {saving ? <Loader2 size={15} /> : <Save size={15} />}
            Registrar portal
          </button>
        </div>
      </div>
    </div>
  );
};

const PortalRow: React.FC<{
  portal: PortalCliente;
  departmentColor: string;
}> = ({ portal, departmentColor }) => {
  const [hovered, setHovered] = useState(false);

  const statusVisual = getStatusCadastralVisual(portal.statusCadastral);
  const rowHoverBackground = toRgba(departmentColor, 0.055);

  return (
    <tr
      style={{
        ...styles.tr,
        background: hovered ? rowHoverBackground : "#ffffff",
        boxShadow: hovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <td style={styles.td}>
        <div style={styles.clientCell}>
          <div
            style={{
              ...styles.clientIcon,
              color: hovered ? departmentColor : "#475569",
              borderColor: hovered ? toRgba(departmentColor, 0.3) : "#dbe4ef",
              background: hovered ? toRgba(departmentColor, 0.07) : "#f8fafc",
            }}
          >
            <Building2 size={15} />
          </div>

          <div style={styles.clientInfo}>
            <div
              style={{
                ...styles.clientName,
                color: hovered ? departmentColor : "#111827",
              }}
            >
              {portal.clienteNome}
            </div>

            <div style={styles.clientMeta}>
              {portal.clienteCnpj}{" "}
              {portal.codigoInterno ? `• ${portal.codigoInterno}` : ""}
            </div>
          </div>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.portalName}>{portal.portalNome}</div>
        <div style={styles.portalUrl}>{portal.portalUrl || "Sem URL"}</div>
      </td>

      <td style={styles.td}>
        <Badge
          background={statusVisual.background}
          color={statusVisual.color}
          border={statusVisual.border}
          icon={statusVisual.icon}
        >
          {getStatusCadastralLabel(portal.statusCadastral)}
        </Badge>
      </td>

      <td style={styles.td}>
        <div style={styles.metricOk}>{portal.documentosAtualizados}</div>
      </td>

      <td style={styles.td}>
        <div
          style={{
            ...styles.metricWarning,
            color: portal.documentosPendentes > 0 ? "#9a3412" : "#64748b",
          }}
        >
          {portal.documentosPendentes}
        </div>
      </td>

      <td style={styles.td}>
        <div
          style={{
            ...styles.metricDanger,
            color: portal.documentosVencidos > 0 ? "#991b1b" : "#64748b",
          }}
        >
          {portal.documentosVencidos}
        </div>
      </td>

      <td style={styles.td}>
        <div
          style={{
            ...styles.metricPurple,
            color:
              portal.documentosAguardandoValidacao > 0 ? "#6d28d9" : "#64748b",
          }}
        >
          {portal.documentosAguardandoValidacao}
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.responsavelText}>{portal.areaResponsavel}</div>
        <div style={styles.clientMeta}>{portal.responsavelNome}</div>
      </td>
    </tr>
  );
};

const GovernancaPortaisClientesLayout: React.FC<
  GovernancaPortaisClientesLayoutProps
> = ({ section }) => {
  const navigate = useNavigate();
  const { departmentColor } = useTheme();
  const auth = useAuth() as any;

  const sessao = auth?.sessao;
  const token = getAuthToken(auth);

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;
  const activeSection = section || getSectionFromPath(window.location.pathname);

  const empresaId = useMemo(() => getEmpresaIdFromSessao(sessao), [sessao]);
  const moduloId = useMemo(() => getModuloIdFromSessao(sessao), [sessao]);
  const moduloDocumentosId = useMemo(
    () => getModuloDocumentosIdFromSessao(sessao),
    [sessao]
  );

  const [filters] = useState<PortalFilterState>(initialFilters);
  const [portais, setPortais] = useState<PortalCliente[]>([]);
  const [documentosCatalogo, setDocumentosCatalogo] = useState<
    DocumentoCatalogo[]
  >([]);
  const [documentosLoading, setDocumentosLoading] = useState(false);
  const [documentosError, setDocumentosError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerSaving, setRegisterSaving] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerForm, setRegisterForm] = useState<RegisterPortalForm>(
    initialRegisterPortalForm
  );

  const loadPortais = async () => {
    setLoading(true);
    setPageError(null);

    try {
      if (!empresaId || !moduloId) {
        throw new Error(
          "Empresa ou módulo de governança não identificado na sessão."
        );
      }

      const params = new URLSearchParams({
        empresa_id: String(empresaId),
        modulo_id: String(moduloId),
      });

      const response = await fetch(`${API_PORTAIS_CLIENTES_URL}?${params}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });

      const data = (await response
        .json()
        .catch(() => null)) as ApiPortaisClientesResponse | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao carregar portais de clientes. Status HTTP ${response.status}.`
        );
      }

      const items = Array.isArray(data.items) ? data.items : [];

      setPortais(items.map(normalizePortalFromApi));
    } catch (error: any) {
      setPageError(error?.message || "Falha ao carregar portais de clientes.");
      setPortais([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentosCatalogo = async () => {
    setDocumentosLoading(true);
    setDocumentosError(null);

    try {
      if (!empresaId) {
        throw new Error("Empresa não identificada na sessão.");
      }

      if (!moduloDocumentosId) {
        throw new Error(
          "Módulo de Certidões e Documentos não identificado na sessão."
        );
      }

      const params = new URLSearchParams({
        empresa_id: String(empresaId),
        modulo_id: String(moduloDocumentosId),
      });

      const response = await fetch(`${API_DOCUMENTOS_URL}?${params}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });

      const data = (await response
        .json()
        .catch(() => null)) as ApiDocumentosResponse | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao carregar documentos. Status HTTP ${response.status}.`
        );
      }

      const normalized = getDocumentosArrayFromApi(data)
        .map(normalizeDocumentoFromApi)
        .filter(Boolean) as DocumentoCatalogo[];

      normalized.sort((a, b) => {
        const cat = a.categoria.localeCompare(b.categoria, "pt-BR");
        if (cat !== 0) return cat;
        return a.nome.localeCompare(b.nome, "pt-BR");
      });

      setDocumentosCatalogo(normalized);
    } catch (error: any) {
      setDocumentosError(error?.message || "Falha ao carregar documentos.");
      setDocumentosCatalogo([]);
    } finally {
      setDocumentosLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;

    async function run() {
      setLoading(true);
      setPageError(null);

      try {
        if (!empresaId || !moduloId) {
          throw new Error(
            "Empresa ou módulo de governança não identificado na sessão."
          );
        }

        const params = new URLSearchParams({
          empresa_id: String(empresaId),
          modulo_id: String(moduloId),
        });

        const response = await fetch(`${API_PORTAIS_CLIENTES_URL}?${params}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });

        const data = (await response
          .json()
          .catch(() => null)) as ApiPortaisClientesResponse | null;

        if (!response.ok || !data?.ok) {
          throw new Error(
            data?.erro ||
              `Falha ao carregar portais de clientes. Status HTTP ${response.status}.`
          );
        }

        if (!alive) return;

        const items = Array.isArray(data.items) ? data.items : [];

        setPortais(items.map(normalizePortalFromApi));
      } catch (error: any) {
        if (!alive) return;

        setPageError(
          error?.message || "Falha ao carregar portais de clientes."
        );
        setPortais([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();

    return () => {
      alive = false;
    };
  }, [empresaId, moduloId, token, reloadKey]);

  useEffect(() => {
    if (!registerOpen) return;
    loadDocumentosCatalogo();
  }, [registerOpen, empresaId, moduloDocumentosId, token]);

  const updateRegisterForm = <K extends keyof RegisterPortalForm>(
    key: K,
    value: RegisterPortalForm[K]
  ) => {
    setRegisterForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleAddDocumentoExigido = (documentoId: number) => {
    setRegisterForm((prev) => {
      if (prev.documentosExigidosIds.includes(documentoId)) return prev;

      return {
        ...prev,
        documentosExigidosIds: [...prev.documentosExigidosIds, documentoId],
      };
    });
  };

  const handleRemoveDocumentoExigido = (documentoId: number) => {
    setRegisterForm((prev) => ({
      ...prev,
      documentosExigidosIds: prev.documentosExigidosIds.filter(
        (id) => id !== documentoId
      ),
    }));
  };

  const filteredBySection = useMemo(() => {
    switch (activeSection) {
      case "pendencias":
        return portais.filter(
          (portal) =>
            portal.documentosPendentes > 0 ||
            portal.statusCadastral === "pendente_documental"
        );

      case "aguardando-validacao":
        return portais.filter(
          (portal) =>
            portal.documentosAguardandoValidacao > 0 ||
            portal.statusCadastral === "aguardando_validacao"
        );

      case "vencidos":
        return portais.filter(
          (portal) =>
            portal.documentosVencidos > 0 ||
            portal.statusCadastral === "documento_vencido"
        );

      case "atualizados":
        return portais.filter((portal) => portal.statusCadastral === "regular");

      case "clientes-portais":
      case "visao-geral":
      default:
        return portais;
    }
  }, [activeSection, portais]);

  const filteredItems = useMemo(() => {
    const clienteSearch = normalizeTextSearch(filters.cliente);
    const portalSearch = normalizeTextSearch(filters.portal);

    return filteredBySection.filter((portal) => {
      const clienteMatch =
        !clienteSearch ||
        [
          portal.clienteNome,
          portal.clienteRazaoSocial,
          portal.clienteCnpj,
          portal.codigoInterno,
        ].some((field) => normalizeTextSearch(field).includes(clienteSearch));

      const portalMatch =
        !portalSearch ||
        [portal.portalNome, portal.portalUrl, portal.usuarioReferencia].some(
          (field) => normalizeTextSearch(field).includes(portalSearch)
        );

      const statusMatch =
        !filters.statusCadastral ||
        portal.statusCadastral === filters.statusCadastral;

      const responsavelMatch =
        !filters.responsavel || portal.areaResponsavel === filters.responsavel;

      const pendenciaMatch =
        !filters.pendencia ||
        (filters.pendencia === "com_pendencia" && hasPendencia(portal)) ||
        (filters.pendencia === "sem_pendencia" && !hasPendencia(portal)) ||
        (filters.pendencia === "vencidos" && portal.documentosVencidos > 0) ||
        (filters.pendencia === "pendente_anexo" &&
          portal.documentosPendentes > 0) ||
        (filters.pendencia === "aguardando_validacao" &&
          portal.documentosAguardandoValidacao > 0);

      return (
        clienteMatch &&
        portalMatch &&
        statusMatch &&
        responsavelMatch &&
        pendenciaMatch
      );
    });
  }, [filteredBySection, filters]);

  const openRegisterOverlay = () => {
    setRegisterError(null);
    setRegisterForm(initialRegisterPortalForm);
    setRegisterOpen(true);
  };

  const closeRegisterOverlay = () => {
    if (registerSaving) return;
    setRegisterOpen(false);
    setRegisterError(null);
  };

  const handleRegisterPortal = async () => {
    setRegisterError(null);

    const clienteNome = String(registerForm.clienteNome ?? "").trim();
    const portalNome = String(registerForm.portalNome ?? "").trim();

    if (!clienteNome) {
      setRegisterError("Informe o nome do cliente.");
      return;
    }

    if (!portalNome) {
      setRegisterError("Informe o nome do portal.");
      return;
    }

    if (!empresaId || !moduloId) {
      setRegisterError(
        "Empresa ou módulo de governança não identificado na sessão."
      );
      return;
    }

    setRegisterSaving(true);

    try {
      const response = await fetch(API_PORTAIS_CLIENTES_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          empresa_id: empresaId,
          modulo_id: moduloId,

          clienteNome,
          clienteRazaoSocial: registerForm.clienteRazaoSocial,
          clienteCnpj: registerForm.clienteCnpj,
          codigoInterno: registerForm.codigoInterno,

          portalNome,
          portalUrl: registerForm.portalUrl,
          usuarioReferencia: registerForm.usuarioReferencia,

          areaResponsavel: registerForm.areaResponsavel,
          responsavelNome: registerForm.responsavelNome,

          statusCadastral: registerForm.statusCadastral,
          observacao: registerForm.observacao,

          documentosExigidosIds: registerForm.documentosExigidosIds,
          documentoModuloId: moduloDocumentosId,
        }),
      });

      const data = (await response
        .json()
        .catch(() => null)) as ApiPostPortalClienteResponse | null;

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.erro ||
            `Falha ao registrar portal de cliente. Status HTTP ${response.status}.`
        );
      }

      const novoPortal = data.portal
        ? normalizePortalFromApi(data.portal)
        : null;

      if (novoPortal) {
        setPortais((prev) => [novoPortal, ...prev]);
      } else {
        setReloadKey((prev) => prev + 1);
      }

      setRegisterOpen(false);
      setRegisterForm(initialRegisterPortalForm);
    } catch (error: any) {
      setRegisterError(
        error?.message || "Falha ao registrar portal de cliente."
      );
    } finally {
      setRegisterSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Portais de Clientes - ${getSectionTitle(activeSection)}`}
      />

      <div style={styles.content}>
        <section style={styles.navigationBar}>
          <nav style={styles.tabs}>
            {sections.map((item) => {
              const active = item.key === activeSection;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.path)}
                  style={{
                    ...styles.tabButton,
                    ...(active
                      ? {
                          background: toRgba(activeDepartmentColor, 0.08),
                          borderColor: activeDepartmentColor,
                          color: activeDepartmentColor,
                        }
                      : {}),
                  }}
                >
                  <span style={styles.tabIcon}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div style={styles.navActions}>
            <button
              type="button"
              style={{
                ...styles.registerButton,
                background: activeDepartmentColor,
                borderColor: activeDepartmentColor,
              }}
              onClick={openRegisterOverlay}
            >
              <Plus size={14} />
              Registrar portal
            </button>
          </div>
        </section>

        {pageError && (
          <section style={styles.pageError}>
            <AlertTriangle size={15} />
            <span>{pageError}</span>
          </section>
        )}

        <section style={styles.tablePanel}>
          <div style={styles.tableHeader}>
            <div>
              <h2 style={styles.tableTitle}>Portais monitorados</h2>
              <span style={styles.tableHint}>
                {loading
                  ? "Carregando registros..."
                  : `${filteredItems.length} ${plural(
                      filteredItems.length,
                      "registro",
                      "registros"
                    )}${
                      activeSection !== "visao-geral"
                        ? ` em ${getSectionTitle(activeSection).toLowerCase()}`
                        : ""
                    }`}
              </span>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Portal</th>
                  <th style={styles.th}>Status cadastral</th>
                  <th style={styles.th}>Atualizados</th>
                  <th style={styles.th}>Pendentes</th>
                  <th style={styles.th}>Vencidos</th>
                  <th style={styles.th}>Validação</th>
                  <th style={styles.th}>Responsável</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} style={styles.emptyCell}>
                      <span style={styles.emptyStateInline}>
                        <Loader2 size={17} />
                        Carregando portais de clientes...
                      </span>
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredItems.map((portal) => (
                    <PortalRow
                      key={portal.id}
                      portal={portal}
                      departmentColor={activeDepartmentColor}
                    />
                  ))}

                {!loading && filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={8} style={styles.emptyCell}>
                      Nenhum portal registrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <RegisterPortalOverlay
        open={registerOpen}
        form={registerForm}
        documentosCatalogo={documentosCatalogo}
        documentosLoading={documentosLoading}
        documentosError={documentosError}
        departmentColor={activeDepartmentColor}
        saving={registerSaving}
        error={registerError}
        onClose={closeRegisterOverlay}
        onChange={updateRegisterForm}
        onAddDocumento={handleAddDocumentoExigido}
        onRemoveDocumento={handleRemoveDocumentoExigido}
        onSubmit={handleRegisterPortal}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  navigationBar: {
    minHeight: 42,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "5px 8px",
  },

  tabs: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    overflowX: "auto",
    minWidth: 0,
  },

  tabButton: {
    height: 30,
    padding: "0 10px",
    border: "1px solid transparent",
    background: "transparent",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
    fontFamily: "inherit",
  },

  tabIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "inherit",
  },

  navActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },

  refreshButton: {
    height: 30,
    padding: "0 10px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  registerButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  pageError: {
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "9px 10px",
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12,
    fontWeight: 800,
  },

  badge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  tablePanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  tableHeader: {
    minHeight: 42,
    padding: "8px 10px",
    borderBottom: "1px solid #dfe3e8",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  tableTitle: {
    margin: 0,
    fontSize: 13.5,
    fontWeight: 900,
    color: "#111827",
  },

  tableHint: {
    display: "block",
    marginTop: 1,
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1080,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 900,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  tr: {
    borderBottom: "1px solid #edf0f2",
    cursor: "default",
    background: "#ffffff",
    transition: "background 140ms ease, box-shadow 140ms ease",
  },

  td: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
  },

  clientCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  selectedDocsArea: {
    width: "100%",
    minHeight: 38,
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  selectedDocsEmpty: {
    width: "100%",
    minHeight: 38,
    padding: "9px 10px",
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    color: "#64748b",
    fontSize: 11.8,
    fontWeight: 750,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
  },

  selectedDocsListPanel: {
    width: "100%",
    marginTop: 10,
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    overflow: "hidden",
  },

  selectedDocsListHeader: {
    minHeight: 44,
    padding: "9px 11px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  selectedDocsTitle: {
    display: "block",
    margin: 0,
    fontSize: 12.6,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.15,
  },

  selectedDocsHint: {
    display: "block",
    marginTop: 2,
    fontSize: 11.2,
    fontWeight: 750,
    color: "#64748b",
    lineHeight: 1.25,
  },

  selectedDocsTableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  selectedDocsTable: {
    width: "100%",
    minWidth: 760,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  selectedDocsTh: {
    height: 32,
    padding: "7px 9px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 10.5,
    fontWeight: 950,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.22,
    whiteSpace: "nowrap",
  },

  selectedDocsThAction: {
    width: 72,
    height: 32,
    padding: "7px 9px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    textAlign: "center",
    fontSize: 10.5,
    fontWeight: 950,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.22,
    whiteSpace: "nowrap",
  },

  selectedDocsTr: {
    borderBottom: "1px solid #edf0f2",
    background: "#ffffff",
  },

  selectedDocsTd: {
    padding: "8px 9px",
    borderRight: "1px solid #f0f2f4",
    verticalAlign: "middle",
    color: "#334155",
    fontSize: 12,
    fontWeight: 750,
    lineHeight: 1.25,
    overflow: "hidden",
  },

  selectedDocsTdAction: {
    width: 72,
    padding: "8px 9px",
    verticalAlign: "middle",
    textAlign: "center",
  },

  selectedDocName: {
    color: "#111827",
    fontSize: 12.2,
    fontWeight: 950,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  selectedDocMeta: {
    marginTop: 2,
    color: "#64748b",
    fontSize: 10.8,
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  docStatusPill: {
    minHeight: 22,
    padding: "0 7px",
    border: "1px solid #dbe4ef",
    background: "#f8fafc",
    color: "#475569",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10.8,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  removeDocButton: {
    width: 28,
    height: 28,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },

  selectedDocChip: {
    width: "100%",
    minHeight: 38,
    padding: "0 8px 0 10px",
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    color: "#334155",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    fontSize: 12,
    fontWeight: 800,
    boxSizing: "border-box",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },

  selectedDocChipText: {
    minWidth: 0,
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  selectedDocRemove: {
    width: 26,
    height: 26,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
  },

  clientIcon: {
    width: 29,
    height: 29,
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition:
      "background  140ms ease, color 140ms ease, border-color 140ms ease",
  },

  clientInfo: {
    minWidth: 0,
  },

  clientName: {
    fontSize: 12.5,
    fontWeight: 900,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  clientMeta: {
    marginTop: 1,
    fontSize: 10.8,
    fontWeight: 650,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalName: {
    fontSize: 12.3,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  portalUrl: {
    marginTop: 1,
    fontSize: 10.8,
    fontWeight: 650,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  metricOk: {
    color: "#166534",
    fontSize: 13,
    fontWeight: 950,
  },

  metricWarning: {
    fontSize: 13,
    fontWeight: 950,
  },

  metricDanger: {
    fontSize: 13,
    fontWeight: 950,
  },

  metricPurple: {
    fontSize: 13,
    fontWeight: 950,
  },

  responsavelText: {
    fontSize: 12.2,
    fontWeight: 850,
    color: "#334155",
  },

  emptyCell: {
    padding: 26,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },

  emptyStateInline: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },

  overlayPanel: {
    width: "min(840px, 100%)",
    maxHeight: "calc(100vh - 36px)",
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "flex-start",
  },

  overlayEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },

  overlayTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.2,
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    fontSize: 12.4,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.35,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #d8dde5",
    background: "#ffffff",
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  overlayError: {
    margin: "12px 18px 0",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "9px 10px",
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12,
    fontWeight: 800,
  },

  formGrid: {
    padding: 18,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },

  formSectionTitle: {
    gridColumn: "1 / -1",
    minHeight: 30,
    padding: "0 9px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#334155",
    fontSize: 12,
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },

  formField: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },

  formFieldWide: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },

  formLabel: {
    fontSize: 11.5,
    fontWeight: 900,
    color: "#334155",
  },

  formInput: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 9px",
    fontSize: 12.5,
    fontWeight: 700,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formTextarea: {
    minHeight: 74,
    resize: "vertical",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "8px 9px",
    fontSize: 12.5,
    fontWeight: 700,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    lineHeight: 1.35,
  },

  docSelectShell: {
    position: "relative",
    width: "100%",
  },

  docSelectButton: {
    width: "100%",
    height: 36,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 800,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  docSelectButtonText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  docDropdown: {
    position: "absolute",
    zIndex: 10000,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: 260,
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    boxShadow: "0 16px 42px rgba(15, 23, 42, 0.18)",
  },

  docSearchBox: {
    height: 38,
    padding: "0 9px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#64748b",
    background: "#f8fafc",
  },

  docSearchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    outline: "none",
    color: "#111827",
    fontSize: 12.4,
    fontWeight: 700,
    fontFamily: "inherit",
  },

  docDropdownError: {
    padding: 10,
    color: "#991b1b",
    background: "#fef2f2",
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12,
    fontWeight: 800,
  },

  docDropdownEmpty: {
    padding: 12,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 750,
  },

  docOption: {
    width: "100%",
    padding: "9px 10px",
    border: "none",
    borderBottom: "1px solid #eef2f7",
    background: "#ffffff",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 2,
    fontFamily: "inherit",
    textAlign: "left",
  },

  docOptionMain: {
    fontSize: 12.4,
    fontWeight: 900,
    color: "#111827",
  },

  docOptionMeta: {
    fontSize: 10.8,
    fontWeight: 700,
    color: "#64748b",
  },

  overlayFooter: {
    padding: "12px 18px 16px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },

  cancelButton: {
    height: 34,
    padding: "0 13px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  saveButton: {
    height: 34,
    padding: "0 14px",
    border: "1px solid",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
};

export default GovernancaPortaisClientesLayout;
